import { useState } from 'react';
import { FiShield } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { FaPlus } from 'react-icons/fa6';
import { Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import CurrencyInput from '../../molecules/CurrencyInput';
import PageFooter from '../../molecules/PageFooter';
import { formatCpfCnpj, formatDate, formatCurrency } from '../../../utils/collateralFormatters';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import { MACHINERY_TYPE_OPTIONS } from '../../../types/Machinery';
import useMachineryPageList from '../../../hooks/useMachineryPageList';

const columns = [
    { id: 'description', title: 'Descrição / Nome', field: 'description', canSort: true, width: '220px' },
    { id: 'machineryType', title: 'Tipo', field: 'machineryType', canSort: false, width: '120px', cssClass: 'text-center' },
    { id: 'legalRegime', title: 'Regime', field: 'legalRegime', canSort: false, width: '150px' },
    { id: 'invoiceValue', title: 'Valor NF', field: 'invoiceValue', canSort: true, width: '120px', cssClass: 'text-right' },
    { id: 'custodyCity', title: 'Cidade', field: 'custodyCity', canSort: true, width: '160px' },
    { id: 'custodyState', title: 'UF', field: 'custodyState', canSort: true, width: '60px', cssClass: 'text-center' },
    { id: 'statusFormatted', title: 'Situação', field: 'statusFormatted', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'createdAt', title: 'Cadastro', field: 'createdAt', canSort: true, width: '90px', cssClass: 'text-center' },
];

const MachineryAssetPageList = () => {
    const navigate = useNavigate();
    const {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading, filterCounts,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        machineryType, setMachineryType,
        archived, setArchived,
        refresh, handleRowClick,
        selectedMachinery, machineryDetail, isLoadingDetail, closePanel,
        apiInfo,
    } = useMachineryPageList();

    const handleTypeFilter = (value: string) => { setPage(1); setMachineryType(value); };
    const handleArchivedFilter = (value: string) => { setPage(1); setArchived(value); };
    const handleClearFilters = () => { setPage(1); setMachineryType(''); setArchived('false'); };

    // Discharge mini-form state
    const [showDischargeForm, setShowDischargeForm] = useState(false);
    const [dischargeDate, setDischargeDate] = useState('');
    const [dischargeValue, setDischargeValue] = useState(0);
    const [dischargeSubmitting, setDischargeSubmitting] = useState(false);

    const resetDischargeForm = () => {
        setShowDischargeForm(false);
        setDischargeDate('');
        setDischargeValue(0);
    };

    const handleDischarge = async () => {
        if (!machineryDetail) return;
        setDischargeSubmitting(true);
        try {
            await collateralApi.dischargeMachinery(machineryDetail.id, {
                discharge_date: dischargeDate,
                discharge_value: dischargeValue || undefined,
            });
            toast.success('Baixa registrada com sucesso');
            resetDischargeForm();
            closePanel();
            refresh();
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 422) {
                    toast.error(e.data?.message || 'Informe a data de baixa');
                    return;
                }
                if (e.status === 409) {
                    toast.error(e.data?.message || 'Maquinário não está apto à baixa');
                    return;
                }
            }
            toast.error('Erro ao registrar baixa');
        } finally {
            setDischargeSubmitting(false);
        }
    };

    const typeCounts = filterCounts?.machineryType || {};
    const totalAll = Object.values(typeCounts).reduce((a: number, b: any) => a + (b?.count || 0), 0);

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={handleClearFilters}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!machineryType && archived === 'false'
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{totalAll}</span>
                        </div>
                    </button>

                    {MACHINERY_TYPE_OPTIONS.length > 0 && (
                        <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">TIPO</div>
                            <div className="space-y-1">
                                {MACHINERY_TYPE_OPTIONS.map(({ value, label }) => (
                                    <button key={value} onClick={() => handleTypeFilter(value)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${machineryType === value
                                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                        <div className="flex items-center justify-between">
                                            <span>{typeCounts[value]?.label || label}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{typeCounts[value]?.count || 0}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">SITUAÇÃO</div>
                        <div className="space-y-1">
                            <button onClick={() => handleArchivedFilter('false')}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${archived === 'false'
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                Ativos
                            </button>
                            <button onClick={() => handleArchivedFilter('true')}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${archived === 'true'
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                Arquivados
                            </button>
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const renderRightPanel = () => {
        if (isLoadingDetail) {
            return <div className="p-4 text-gray-500 dark:text-gray-400">Carregando...</div>;
        }
        if (!machineryDetail) {
            return <div className="p-4 text-gray-500 dark:text-gray-400">Selecione um maquinário</div>;
        }

        const d = machineryDetail;
        return (
            <div className="p-4 space-y-4 overflow-auto h-full">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{d.name || d.description}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {d.machineryTypeFormatted} • {d.custody?.city || '—'} • {d.custody?.state || '—'}
                    </p>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Dados do Maquinário</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500 dark:text-gray-400">Tipo:</span> <span className="dark:text-white">{d.machineryTypeFormatted}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Regime:</span> <span className="dark:text-white">{d.legalRegimeFormatted || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Marca:</span> <span className="dark:text-white">{d.brand || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Modelo:</span> <span className="dark:text-white">{d.model || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Ano fabricação:</span> <span className="dark:text-white">{d.yearManufacture ?? '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Ano modelo:</span> <span className="dark:text-white">{d.yearModel ?? '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Nº série / chassi:</span> <span className="dark:text-white">{d.serialNumber || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Código FINAME:</span> <span className="dark:text-white">{d.finameCode || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Cor:</span> <span className="dark:text-white">{d.color || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Valor NF:</span> <span className="dark:text-white">{d.invoiceValue != null ? formatCurrency(d.invoiceValue) : '—'}</span></div>
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Descrição:</span> <span className="dark:text-white">{d.description || '—'}</span></div>
                    </div>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Guarda</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Endereço:</span> <span className="dark:text-white">{d.custody?.address || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Cidade:</span> <span className="dark:text-white">{d.custody?.city || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">UF:</span> <span className="dark:text-white">{d.custody?.state || '—'}</span></div>
                    </div>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cartórios</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Cartório RTD:</span> <span className="dark:text-white">{d.rtdRegistryOffice || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Cartório RI:</span> <span className="dark:text-white">{d.registryOffice || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">UF:</span> <span className="dark:text-white">{d.registryState || '—'}</span></div>
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Matrícula:</span> <span className="dark:text-white">{d.registrationNumber || '—'}</span></div>
                    </div>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Proprietários</h4>
                    {d.owners && d.owners.length > 0 ? (
                        d.owners.map((o: any, i: number) => (
                            <div key={o.personId || o.document || i} className="text-sm">
                                <div className="dark:text-white">{o.name}</div>
                                {o.document && <div className="text-xs text-gray-500 dark:text-gray-400">{formatCpfCnpj(o.document)}</div>}
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum proprietário</p>
                    )}
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Obrigações</h4>
                    {d.obligations && d.obligations.length > 0 ? (
                        d.obligations.map((o: any, i: number) => (
                            <div key={o.creditOperationId || i} className="bg-gray-50 dark:bg-slate-700 rounded-lg p-2 text-sm">
                                <div className="dark:text-white font-medium">{o.creditOperationId}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {o.allocatedPercentage != null ? `${o.allocatedPercentage}%` : '—'} • {o.statusFormatted || o.status}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma obrigação vinculada</p>
                    )}
                </div>

                {d.costs && d.costs.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Custos / Registros</h4>
                        {d.costs.map((c: any, i: number) => (
                            <div key={c.id || i} className="bg-gray-50 dark:bg-slate-700 rounded-lg p-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="dark:text-white font-medium">{c.costTypeFormatted}</span>
                                    <span className="dark:text-white">{formatCurrency(c.value)}</span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {c.costDate && formatDate(c.costDate)}
                                    {c.registrationNumber && ` • Nº ${c.registrationNumber}`}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Baixa</h4>
                    {d.discharged ? (
                        <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 text-gray-700 dark:bg-slate-600 dark:text-gray-200">
                                    Baixado
                                </span>
                                {d.dischargeDate && <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(d.dischargeDate)}</span>}
                            </div>
                            {d.dischargeValue != null && (
                                <div><span className="text-gray-500 dark:text-gray-400">Valor da baixa:</span> <span className="dark:text-white">{formatCurrency(d.dischargeValue)}</span></div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {!showDischargeForm ? (
                                <button onClick={() => setShowDischargeForm(true)}
                                    className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
                                    Registrar baixa
                                </button>
                            ) : (
                                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 space-y-2">
                                    <TextEdit label="Data da baixa" type="date" value={dischargeDate}
                                        onChange={e => setDischargeDate(e.target.value)} />
                                    <CurrencyInput label="Valor da baixa (opcional)" fractionDigits={2}
                                        value={dischargeValue} onChange={setDischargeValue} />
                                    <div className="flex items-center gap-2">
                                        <button onClick={handleDischarge} disabled={dischargeSubmitting || !dischargeDate}
                                            className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                            {dischargeSubmitting ? 'Salvando...' : 'Confirmar baixa'}
                                        </button>
                                        <button onClick={resetDischargeForm} disabled={dischargeSubmitting}
                                            className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg text-sm font-medium">
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            {isLoading && <Loading />}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={!!selectedMachinery}
                onCloseRightPanel={closePanel}
                rightPanel={renderRightPanel()}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Maquinários</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <button onClick={() => navigate('/collateral/machineries/new')}
                                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                    <FaPlus size={12} /> Novo
                                </button>
                                <div className="w-96">
                                    <TextEdit label="Buscar descrição, cidade, proprietário..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum maquinário encontrado</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'} ${selectedMachinery?.id === row.id ? 'bg-blue-50 dark:bg-slate-700' : ''}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium text-blue-700 dark:text-blue-400 truncate" title={row.description || row.name || ''}>{row.description || row.name || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                                {row.machineryTypeFormatted}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{row.legalRegimeFormatted || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 text-right">{row.invoiceValue != null ? formatCurrency(row.invoiceValue) : '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 truncate" title={row.custodyCity || ''}>{row.custodyCity || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 text-center">{row.custodyState || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${row.discharged
                                                ? 'bg-gray-200 text-gray-700 dark:bg-slate-600 dark:text-gray-200'
                                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'}`}>
                                                {row.statusFormatted}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400 text-center">{formatDate(row.createdAt)}</td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ refresh }} />
        </>
    );
};

export default MachineryAssetPageList;
